import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { productionAPI, stockAPI } from '../services/api';
import { Spinner } from '../components/Spinner';
import { Plus, Package, TrendingUp, AlertCircle, BarChart3, ScanBarcode } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import BarcodeScanPanel from '../components/BarcodeScanPanel';

const headerCellClass = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500';
const bodyCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-slate-500';

const ProductionLayout = () => (
  <Routes>
    <Route index element={<ProductionOverview />} />
    <Route path="orders" element={<ProductionOrders />} />
    <Route path="barcode" element={<ProductionBarcode />} />
    <Route path="materials" element={<MaterialUsage />} />
    <Route path="costs" element={<CostAnalysis />} />
    <Route path="efficiency" element={<EfficiencyReport />} />
  </Routes>
);

const ProductionOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await productionAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Üretim istatistikleri alınamadı:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Üretim</h1>
            <p className="mt-1 text-sm text-slate-500">Üretim emirleri, malzeme kullanımı ve verimliliği tek yapı içinde izleyin.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate('/production/barcode')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Barkod Çıkış</button>
            <button onClick={() => navigate('/production/orders')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Emirler</button>
            <button onClick={() => navigate('/production/materials')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Malzeme</button>
            <button onClick={() => navigate('/production/efficiency')} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Verimlilik</button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-100 p-3 text-blue-600"><Package className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Toplam Emir</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.totalOrders || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-yellow-100 p-3 text-yellow-600"><AlertCircle className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Beklemede</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.pendingOrders || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-orange-100 p-3 text-orange-600"><TrendingUp className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Devam Eden</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.inProgressOrders || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-green-100 p-3 text-green-600"><BarChart3 className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Tamamlanan</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.completedOrders || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Maliyet Özeti</h2>
          <div className="space-y-4">
            <div className="mt-4 flex justify-between items-center rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-slate-500">Toplam Üretim Maliyeti</span>
              <span className="text-xl font-bold text-slate-900">{formatCurrency(stats.totalCost)}</span>
            </div>
            <div className="flex justify-between items-center rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-slate-500">Emir Başına Ortalama</span>
              <span className="text-lg font-semibold text-slate-900">{formatCurrency(stats.avgCost)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Hızlı İşlemler</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <button onClick={() => navigate('/production/barcode')} className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-4 text-left hover:bg-indigo-100">
              <p className="text-sm font-semibold text-indigo-900 flex items-center gap-2"><ScanBarcode className="h-4 w-4" /> Barkod Çıkış</p>
              <p className="mt-1 text-xs text-indigo-700">Üretime malzeme okut</p>
            </button>
            <button onClick={() => navigate('/production/orders')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Emirler</p><p className="mt-1 text-xs text-slate-500">Üretim akışı</p></button>
            <button onClick={() => navigate('/production/materials')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Malzeme</p><p className="mt-1 text-xs text-slate-500">Tüketim özeti</p></button>
            <button onClick={() => navigate('/production/costs')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Maliyet</p><p className="mt-1 text-xs text-slate-500">Günlük analiz</p></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductionOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: 1,
    material_cost: 0,
    labor_cost: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchProducts();
  }, []);

  const getStatusLabel = (status) => {
    const statusMap = {
      'completed': 'Tamamlandı',
      'in_progress': 'Devam Ediyor',
      'pending': 'Bekliyor',
      'cancelled': 'İptal Edildi'
    };
    return statusMap[status] || status;
  };

  const fetchOrders = async () => {
    try {
      const response = await productionAPI.getOrders();
      setOrders(response.data.orders || []);
    } catch (error) {
      toast.error('Üretim emirleri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Hem ürünleri hem de hammaddeleri çekiyoruz
      const [prodRes, matRes] = await Promise.all([
        fetch('/api/sales/products', { headers }),
        fetch('/api/stock/materials', { headers })
      ]);
      
      const prodData = await prodRes.json();
      const matData = await matRes.json();
      
      const prods = Array.isArray(prodData.products) ? prodData.products : (Array.isArray(prodData) ? prodData : []);
      const mats = Array.isArray(matData) ? matData : [];
      
      // Ürünler ve hammaddeleri birleştiriyoruz
      const combined = [
        ...prods.map(p => ({ ...p, type: 'product' })),
        ...mats.map(m => ({ ...m, type: 'material' }))
      ];
      
      setProducts(combined);
    } catch (error) {
      console.error('Ürünler ve hammaddeler alınamadı:', error);
      toast.error('Ürün listesi yüklenemedi');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await productionAPI.updateOrderStatus(id, status);
      toast.success('Durum güncellendi');
      fetchOrders();
    } catch (error) {
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await productionAPI.createOrder(formData);
      toast.success('Üretim emri oluşturuldu');
      setShowModal(false);
      fetchOrders();
    } catch (error) {
      toast.error('Emir oluşturulurken hata oluştu');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Üretim Emirleri</h1>
          <p className="mt-1 text-sm text-slate-500">Durum, maliyet ve tarih bilgilerini tablo halinde gösterir.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/production/barcode')}
            className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
          >
            <ScanBarcode className="w-4 h-4 mr-2" />
            Barkod Çıkış
          </button>
          <button 
            onClick={() => setShowModal(true)}
            data-help-target="production-order-create"
            className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Emir Oluştur
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Yeni Üretim Emri</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ürün / Hammadde *</label>
                <select 
                  required 
                  className="w-full border rounded-lg px-3 py-2"
                  value={formData.product_id}
                  onChange={e => setFormData({...formData, product_id: e.target.value})}
                >
                  <option value="">Seçin...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.code || p.sku})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Miktar *</label>
                  <input 
                    type="number" required min="1"
                    className="w-full border rounded-lg px-3 py-2"
                    value={formData.quantity}
                    onChange={e => setFormData({...formData, quantity: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Başlangıç *</label>
                  <input 
                    type="date" required
                    className="w-full border rounded-lg px-3 py-2"
                    value={formData.start_date}
                    onChange={e => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">İptal</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className={headerCellClass}>Emir No</th>
              <th className={headerCellClass}>Ürün</th>
              <th className={headerCellClass}>Adet</th>
              <th className={headerCellClass}>Toplam Maliyet</th>
              <th className={headerCellClass}>Durum</th>
              <th className={headerCellClass}>Başlangıç</th>
              <th className={headerCellClass}>Bitiş</th>
              <th className={headerCellClass}>İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                  {order.order_number}
                </td>
                <td className={bodyCellClass}>
                  {order.product_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {order.quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {formatCurrency(order.total_cost)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${
                    order.status === 'completed' ? 'bg-green-100 text-green-800' :
                    order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {getStatusLabel(order.status)}
                  </span>
                </td>
                <td className={bodyCellClass}>
                  {order.start_date ? new Date(order.start_date).toLocaleDateString('tr-TR') : 'Yok'}
                </td>
                <td className={bodyCellClass}>
                  {order.end_date ? new Date(order.end_date).toLocaleDateString('tr-TR') : 'Yok'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    {order.status === 'pending' && (
                      <button 
                        onClick={() => handleUpdateStatus(order.id, 'in_progress')}
                        className="text-blue-600 hover:text-blue-900 bg-blue-50 px-2 py-1 rounded"
                      >
                        İşlemi Başlat
                      </button>
                    )}
                    {order.status === 'in_progress' && (
                      <button 
                        onClick={() => handleUpdateStatus(order.id, 'completed')}
                        className="text-green-600 hover:text-green-900 bg-green-50 px-2 py-1 rounded"
                      >
                        Tamamla
                      </button>
                    )}
                    {(order.status === 'pending' || order.status === 'in_progress') && (
                      <button 
                        onClick={() => handleUpdateStatus(order.id, 'cancelled')}
                        className="text-red-600 hover:text-red-900 bg-red-50 px-2 py-1 rounded"
                      >
                        İptal
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )) || <tr><td colSpan="8" className="py-4 text-center text-slate-500">Üretim emri bulunamadı</td></tr>}
          </tbody>
        </table>
        {orders.length === 0 && (
          <div className="py-10 text-center text-slate-500 border-t">
            Üretim emri bulunamadı
          </div>
        )}
      </div>
    </div>
  );
};

const ProductionBarcode = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ordersRes, warehousesRes] = await Promise.all([
          productionAPI.getOrders({ limit: 100 }),
          stockAPI.getWarehouses(),
        ]);
        const activeOrders = (ordersRes.data.orders || []).filter(
          (order) => order.status === 'pending' || order.status === 'in_progress'
        );
        setOrders(activeOrders);
        setWarehouses(warehousesRes.data || []);
      } catch (error) {
        toast.error('Barkod ekranı verileri alınamadı');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ScanBarcode className="text-indigo-600" />
              Üretim Barkod Çıkışı
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Barkod okutunca stoktan malzeme düşülür ve seçili üretim emrine maliyet yazılır.
            </p>
          </div>
          <button
            onClick={() => navigate('/production/orders')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Emirlere Dön
          </button>
        </div>
      </div>

      <BarcodeScanPanel
        title="Üretime Malzeme Çıkışı"
        subtitle="Aktif emri seçin, barkodu okutun, miktarı onaylayın."
        mode="out"
        allowModeSwitch={false}
        requireOrder
        warehouses={warehouses}
        orders={orders}
        onLookup={async (code) => {
          const response = await stockAPI.getMaterialByBarcode(code);
          return response.data;
        }}
        onConfirm={async ({ barcode, quantity, warehouse_id, order_id }) => {
          const response = await productionAPI.barcodeConsume({
            barcode,
            quantity,
            warehouse_id,
            order_id,
          });
          return response.data;
        }}
      />

      {orders.length === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Aktif (bekleyen/devam eden) üretim emri yok. Önce emir oluşturun veya durumu güncelleyin.
        </div>
      )}
    </div>
  );
};

const MaterialUsage = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await productionAPI.getMaterialUsage();
      setMaterials(response.data);
    } catch (error) {
      toast.error('Malzeme kullanım verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Malzeme Kullanımı</h1>
        <p className="mt-1 text-sm text-slate-500">Ürün bazlı tüketim ve birim maliyet özeti.</p>
      </div>
      
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className={headerCellClass}>Ürün</th>
              <th className={headerCellClass}>Stok Kodu</th>
              <th className={headerCellClass}>Toplam Miktar</th>
              <th className={headerCellClass}>Toplam Maliyet</th>
              <th className={headerCellClass}>Birim Ort.</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {materials.map((material, index) => (
              <tr key={index} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                  {material.product_name}
                </td>
                <td className={bodyCellClass}>
                  {material.sku}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {material.total_quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {formatCurrency(material.total_material_cost)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {formatCurrency(material.avg_material_cost_per_unit)}
                </td>
              </tr>
            )) || <tr><td colSpan="5" className="py-4 text-center text-slate-500">Malzeme kullanım verisi bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CostAnalysis = () => {
  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCostData();
  }, []);

  const fetchCostData = async () => {
    try {
      const response = await productionAPI.getCostAnalysis();
      setCostData(response.data);
    } catch (error) {
      toast.error('Maliyet analizi verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Maliyet Analizi</h1>
        <p className="mt-1 text-sm text-slate-500">Son 30 günlük maliyet akışını tablo halinde inceler.</p>
      </div>
      
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4 text-slate-900">Günlük Üretim Maliyetleri</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Tarih</th>
                <th className={headerCellClass}>Emir Sayısı</th>
                <th className={headerCellClass}>Günlük Maliyet</th>
                <th className={headerCellClass}>Ortalama Emir</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costData.slice(0, 10).map((data, index) => (
                <tr key={index} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {new Date(data.date).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {data.order_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {formatCurrency(data.daily_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {formatCurrency(data.avg_order_cost)}
                  </td>
                </tr>
              )) || <tr><td colSpan="4" className="py-4 text-center text-slate-500">Maliyet verisi bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const EfficiencyReport = () => {
  const [efficiencyData, setEfficiencyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEfficiencyData();
  }, []);

  const fetchEfficiencyData = async () => {
    try {
      const response = await productionAPI.getEfficiencyReport();
      setEfficiencyData(response.data);
    } catch (error) {
      toast.error('Verimlilik raporu alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Üretim Verimlilik Raporu</h1>
        <p className="mt-1 text-sm text-slate-500">Üretim günü ve maliyet ilişkisini tablo üzerinde gösterir.</p>
      </div>
      
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className={headerCellClass}>Emir No</th>
              <th className={headerCellClass}>Ürün</th>
              <th className={headerCellClass}>Adet</th>
              <th className={headerCellClass}>Toplam Maliyet</th>
              <th className={headerCellClass}>Başlangıç</th>
              <th className={headerCellClass}>Bitiş</th>
              <th className={headerCellClass}>Üretim Günü</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {efficiencyData.map((data, index) => (
              <tr key={index} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                  {data.order_number}
                </td>
                <td className={bodyCellClass}>
                  {data.product_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {data.quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {formatCurrency(data.total_cost)}
                </td>
                <td className={bodyCellClass}>
                  {data.start_date ? new Date(data.start_date).toLocaleDateString('tr-TR') : 'Yok'}
                </td>
                <td className={bodyCellClass}>
                  {data.end_date ? new Date(data.end_date).toLocaleDateString('tr-TR') : 'Yok'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {data.production_days ? `${data.production_days} gün` : 'Yok'}
                </td>
              </tr>
            )) || <tr><td colSpan="7" className="py-4 text-center text-slate-500">Verimlilik verisi bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionLayout;
